// Step 0.5: the playbook as a short deck the owner can read in ten minutes and react to section by section.
//
// Each section is one slide in plain English (lib/playbook.ts). For each, the owner says whether it is a fair
// description (spot on, nearly, wrong) and adds notes, typed or spoken. They can say yes or no to real example firms
// and ask for an automation to need (or not need) their approval. Every reaction is saved as it is made
// (act: playbook_feedback), so they can stop and come back. At the end they either approve the draft, or send the
// feedback and the strategy agent drafts the next version (act: send_playbook_feedback).
//
// The deck takes the business's own brand colour and heading font from the draft when they are legible, so it reads
// as their playbook, and it downloads as a PowerPoint in the same branding. The detailed settings are one click away.
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { Crm } from "../lib/crm";
import type { ConfigVersion, JsonObject, Organisation, PlaybookFeedbackInput, PlaybookFeedbackRow, PlaybookNote } from "../lib/types";
import { buildPlaybook, VERDICTS, type Confidence, type Playbook, type PlaybookSection, type Verdict } from "../lib/playbook";
import { brandTokens } from "../lib/brand";
import { countChanges, diffConfig } from "../lib/config";
import { fmtDateTime } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { useDictation } from "../lib/speech";
import { offerFromConfig } from "../lib/offerWorkshop";
import { Async, Notice } from "./ui";
import { ConfigSections } from "./PlaybookReview";
import "./PlaybookDeck.css";

type Firm = Pick<Organisation, "id" | "name" | "city" | "segment" | "stage" | "fit_score" | "tier" | "attributes">;

/** Lead stages at or after first contact: firms there are not shown as examples. */
function contactedStages(config: JsonObject): string[] {
  const p = (config.pipeline ?? {}) as JsonObject;
  const keys = (Array.isArray(p.lead_stages) ? p.lead_stages : []).map((s) => (typeof s === "string" ? s : String((s as JsonObject)?.key ?? "")));
  const first = keys.findIndex((k) => /sequence|contact|engaged|meeting|met|nurture|closed/.test(k));
  return first >= 0 ? keys.slice(first) : [];
}

export function PlaybookDeck({ crm, draft, active, onDecided }: { crm: Crm; draft: ConfigVersion; active: JsonObject | null; onDecided: () => void }) {
  const data = useAsync(async () => {
    const [tenant, feedback, firms] = await Promise.all([
      crm.tenant(),
      // Not caught: a deck that silently shows no feedback would invite the owner to start again.
      crm.playbookFeedback(),
      crm.playbookFirms(5, contactedStages(draft.config)).catch(() => [] as Firm[]),
    ]);
    return { name: tenant?.name ?? "", feedback, firms };
  }, [crm, draft.version]);
  return (
    <Async state={data} label="Loading your playbook">
      {(d) => <Deck crm={crm} draft={draft} active={active} onDecided={onDecided} name={d.name} feedback={d.feedback} firms={d.firms} />}
    </Async>
  );
}

// ------------------------------------------------------------------------------------------------ state
interface Entry {
  verdict: string | null;
  notes: PlaybookNote[];
}
const keyOf = (kind: string, section: string, item = "") => `${kind}:${section}:${item}`;

function entriesFor(rows: PlaybookFeedbackRow[], version: number): Map<string, Entry> {
  const m = new Map<string, Entry>();
  for (const r of rows) if (r.version === version) m.set(keyOf(r.kind, r.section, r.item), { verdict: r.verdict, notes: Array.isArray(r.notes) ? r.notes : [] });
  return m;
}

/** Notes left on the version before this one, by section: shown as "last time you said". */
function previousNotes(rows: PlaybookFeedbackRow[], version: number): { version: number; bySection: Map<string, string[]> } | null {
  const earlier = rows.filter((r) => r.version < version);
  if (!earlier.length) return null;
  const v = Math.max(...earlier.map((r) => r.version));
  const bySection = new Map<string, string[]>();
  for (const r of earlier) {
    if (r.version !== v || r.kind !== "section") continue;
    const texts = (Array.isArray(r.notes) ? r.notes : []).map((n) => n.text);
    if (r.verdict === "nearly" || r.verdict === "wrong" || texts.length) bySection.set(r.section, texts.length ? texts : [r.verdict === "wrong" ? "You marked this wrong." : "You marked this nearly right."]);
  }
  return { version: v, bySection };
}

const CONF_LABEL: Record<Confidence, string> = { sure: "Sure", likely: "Fairly sure", guess: "Our guess" };

// ------------------------------------------------------------------------------------------------ the deck
function Deck({
  crm,
  draft,
  active,
  onDecided,
  name,
  feedback,
  firms,
}: {
  crm: Crm;
  draft: ConfigVersion;
  active: JsonObject | null;
  onDecided: () => void;
  name: string;
  feedback: PlaybookFeedbackRow[];
  firms: Firm[];
}) {
  const pb: Playbook = useMemo(() => buildPlaybook(draft.config, name), [draft.config, name]);
  // The playbook in use (after onboarding): the owner reads it and asks for changes; there is nothing to approve.
  const live = draft.status === "approved";
  const total = pb.sections.length;
  const [entries, setEntries] = useState(() => entriesFor(feedback, draft.version));
  const prev = useMemo(() => previousNotes(feedback, draft.version), [feedback, draft.version]);
  const hasActive = Boolean(active && Object.keys(active).length);
  const changed = useMemo(() => {
    const s = new Set<string>();
    if (!hasActive) return s;
    for (const sec of pb.sections) if (sec.configKeys.length && countChanges(diffConfig(active, draft.config, sec.configKeys)) > 0) s.add(sec.id);
    return s;
  }, [pb, active, draft.config, hasActive]);

  const verdictOf = useCallback((id: string) => (entries.get(keyOf("section", id))?.verdict ?? null) as Verdict | null, [entries]);
  const reviewed = pb.sections.filter((s) => verdictOf(s.id)).length;
  const firstOpen = pb.sections.findIndex((s) => !verdictOf(s.id));
  // 0 = cover, 1..total = sections, total + 1 = sign off.
  const [idx, setIdx] = useState(() => (reviewed === 0 ? 0 : firstOpen >= 0 ? firstOpen + 1 : total + 1));
  const [view, setView] = useState<"deck" | "settings">("deck");
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const brand = useMemo(() => brandTokens(draft.config), [draft.config]);
  useBrandFonts(brand.headingFont);
  const style = {
    ...(brand.accentLight ? { "--deck-accent-light": brand.accentLight } : {}),
    ...(brand.accentDark ? { "--deck-accent-dark": brand.accentDark } : {}),
    ...(brand.headingFont ? { "--deck-font-head": `"${brand.headingFont}"` } : {}),
  } as CSSProperties;

  // Saves run one at a time per thing reacted to, so two quick clicks land in the order they were made; a failure
  // puts back what was there before that click, unless a later click has already changed it.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const queues = useRef(new Map<string, Promise<unknown>>());
  const seq = useRef(new Map<string, number>());
  const enqueue = useCallback((k: string, next: Entry | null, call: () => Promise<unknown>) => {
    const before = entriesRef.current.get(k);
    const mine = (seq.current.get(k) ?? 0) + 1;
    seq.current.set(k, mine);
    const set = (e: Entry | null | undefined) =>
      setEntries((m) => {
        const n = new Map(m);
        if (e) n.set(k, e);
        else n.delete(k);
        entriesRef.current = n;
        return n;
      });
    set(next);
    setError(null);
    const run = (queues.current.get(k) ?? Promise.resolve()).then(call).then(
      () => true,
      (e: unknown) => {
        if (seq.current.get(k) === mine) set(before);
        setError((e as Error).message);
        return false;
      },
    );
    queues.current.set(k, run);
    return run;
  }, []);

  const save = useCallback(
    (f: Omit<PlaybookFeedbackInput, "version">) => {
      const k = keyOf(f.kind, f.section, f.item ?? "");
      const before = entriesRef.current.get(k);
      const next: Entry = {
        verdict: f.verdict === "clear" ? null : (f.verdict ?? before?.verdict ?? null),
        notes: f.note?.trim() ? [...(before?.notes ?? []), { text: f.note.trim(), via: f.via ?? "typed", by: "you", at: new Date().toISOString() }] : (before?.notes ?? []),
      };
      return enqueue(k, next, () => crm.givePlaybookFeedback({ ...f, version: draft.version }));
    },
    [crm, draft.version, enqueue],
  );

  const removeNote = useCallback(
    (sectionId: string, index: number) => {
      const k = keyOf("section", sectionId);
      const before = entriesRef.current.get(k);
      if (!before) return Promise.resolve(false);
      return enqueue(k, { ...before, notes: before.notes.filter((_, i) => i !== index) }, () => crm.removePlaybookNote({ version: draft.version, kind: "section", section: sectionId, index }));
    },
    [crm, draft.version, enqueue],
  );

  const download = async () => {
    setExporting(true);
    setError(null);
    try {
      const { downloadPlaybookPptx } = await import("../lib/playbookPptx");
      await downloadPlaybookPptx(pb, draft.config, draft.version);
    } catch (e) {
      setError(`The PowerPoint couldn't be made: ${(e as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  const section = idx >= 1 && idx <= total ? pb.sections[idx - 1]! : null;

  return (
    <div className="deck" style={style}>
      <header className="deck-head">
        <div>
          <p className="deck-kicker">
            Your playbook · version {draft.version}
            {live ? (draft.decided_at ? ` · in use since ${fmtDateTime(draft.decided_at)}` : " · in use") : draft.created_at ? ` · drafted ${fmtDateTime(draft.created_at)}` : ""}
          </p>
          <p className="deck-progress" aria-live="polite">
            {reviewed} of {total} sections reviewed
          </p>
        </div>
        <div className="deck-head-actions">
          <button type="button" className="btn" aria-pressed={view === "settings"} onClick={() => setView(view === "deck" ? "settings" : "deck")}>
            {view === "deck" ? "Detailed settings" : "Back to the playbook"}
          </button>
          <button type="button" className="btn" onClick={download} disabled={exporting}>
            {exporting ? "Making the PowerPoint…" : "Download as PowerPoint"}
          </button>
        </div>
      </header>
      {!live && draft.change_note && view === "deck" && (
        <p className="deck-change-note">
          <strong>What the strategy agent changed: </strong>
          {draft.change_note}
        </p>
      )}
      <Notice text={error} tone="bad" />

      {view === "settings" ? (
        <SettingsView draft={draft} active={hasActive ? active : null} />
      ) : (
        <div className="deck-body">
          <nav className="deck-rail" aria-label="Playbook sections">
            <ol>
              <RailItem n="" title="Introduction" current={idx === 0} onClick={() => setIdx(0)} />
              {pb.sections.map((s, i) => (
                <RailItem key={s.id} n={s.number} title={s.title} current={idx === i + 1} verdict={verdictOf(s.id)} changed={changed.has(s.id)} onClick={() => setIdx(i + 1)} />
              ))}
              <RailItem n="" title={live ? "Ask for changes" : "Sign off"} current={idx === total + 1} onClick={() => setIdx(total + 1)} />
            </ol>
          </nav>

          <div className="deck-main">
            <article className="slide" aria-labelledby="slide-title">
              {idx === 0 && <Cover pb={pb} version={draft.version} live={live} />}
              {section && <Slide section={section} firms={firms} entries={entries} save={save} config={draft.config} />}
              {idx === total + 1 && <SignOff pb={pb} verdictOf={verdictOf} entries={entries} crm={crm} version={draft.version} onDecided={onDecided} setError={setError} goTo={(i) => setIdx(i + 1)} live={live} />}
            </article>

            {section && <VerdictBar key={section.id} section={section} verdict={verdictOf(section.id)} save={save} />}

            <div className="deck-nav">
              <button type="button" className="btn" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}>
                Back
              </button>
              {idx <= total && (
                <button type="button" className="btn btn-primary" onClick={() => setIdx(idx + 1)}>
                  {idx === 0 ? "Start" : idx === total ? (live ? "Go to ask for changes" : "Go to sign off") : "Next"}
                </button>
              )}
            </div>
          </div>

          {section && (
            <aside className="deck-side" aria-label="About this section">
              <SidePanel section={section} entry={entries.get(keyOf("section", section.id))} prev={prev} changed={changed.has(section.id)} onRemove={(i) => removeNote(section.id, i)} />
            </aside>
          )}
        </div>
      )}
    </div>
  );
}

function useBrandFonts(family: string | null) {
  useEffect(() => {
    if (!family || typeof document === "undefined") return;
    const id = "deck-brand-font";
    const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;600;700&display=swap`;
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
  }, [family]);
}

function RailItem({ n, title, current, verdict, changed, onClick }: { n: string; title: string; current: boolean; verdict?: Verdict | null; changed?: boolean; onClick: () => void }) {
  const tag = verdict ? VERDICTS.find((v) => v.key === verdict)?.label : null;
  // On a phone the list scrolls sideways: keep the current section in view.
  const ref = useCallback(
    (el: HTMLButtonElement | null) => {
      if (current && el && typeof el.scrollIntoView === "function" && el.parentElement?.parentElement && el.parentElement.parentElement.scrollWidth > el.parentElement.parentElement.clientWidth) {
        el.scrollIntoView({ block: "nearest", inline: "center" });
      }
    },
    [current],
  );
  return (
    <li>
      <button ref={ref} type="button" className={`rail-item${current ? " is-current" : ""}`} aria-current={current ? "step" : undefined} onClick={onClick}>
        <span className="rail-notch" aria-hidden="true" />
        {n && <span className="rail-n">{n}</span>}
        <span className="rail-title">{title}</span>
        {changed && (
          <span className="rail-changed" title="Changed in this draft">
            <span className="sr-only">Changed in this draft</span>
          </span>
        )}
        {tag && <span className={`rail-tag rail-tag-${verdict}`}>{tag}</span>}
      </button>
    </li>
  );
}

// ------------------------------------------------------------------------------------------------ slides
function Cover({ pb, version, live }: { pb: Playbook; version: number; live: boolean }) {
  if (live) {
    return (
      <div className="slide-cover">
        <p className="slide-ref">{pb.name} · in use</p>
        <h2 id="slide-title" className="slide-display">
          How {pb.name} wins work
        </h2>
        <p className="slide-lead">
          This is the playbook Ogma works from today. If something in your business has changed, or a section never quite fitted, mark it and say what's different.
        </p>
        <ul className="slide-cover-steps">
          <li>Read the sections that matter to you; you don't need to go through all {pb.sections.length}.</li>
          <li>Mark what's no longer right and add a note in your own words. You can speak it.</li>
          <li>Ask for changes and the strategy agent drafts version {version + 1} for you to approve. This version stays in use until you do.</li>
        </ul>
      </div>
    );
  }
  return (
    <div className="slide-cover">
      <p className="slide-ref">{pb.name}</p>
      <h2 id="slide-title" className="slide-display">
        How {pb.name} wins work
      </h2>
      <p className="slide-lead">
        This is how we understand your business, written so you can tell us where we've got it wrong. Ogma will find, contact and follow up the way this playbook says, so it's worth ten minutes.
      </p>
      <ul className="slide-cover-steps">
        <li>Read each of the {pb.sections.length} short sections.</li>
        <li>Say whether it's spot on, nearly right or wrong, and add a note in your own words. You can speak it.</li>
        <li>At the end, approve it, or send your notes and get version {version + 1}.</li>
      </ul>
      {!pb.narrative && <p className="slide-hint">This draft was written straight from your settings, so some sections are plainer than they will be once the strategy agent has written them up.</p>}
    </div>
  );
}

function Slide({ section: s, firms, entries, save, config }: { section: PlaybookSection; firms: Firm[]; entries: Map<string, Entry>; save: (f: Omit<PlaybookFeedbackInput, "version">) => Promise<boolean>; config?: JsonObject }) {
  return (
    <>
      <p className="slide-ref">
        {s.number} · {s.title}
      </p>
      <h2 id="slide-title" className="slide-title">
        {s.headline}
      </h2>
      {s.lead && <p className="slide-lead">{s.lead}</p>}
      {s.statements.length > 0 && (
        <ul className="slide-statements">
          {s.statements.map((x, i) => (
            <li key={i}>
              <span>{x.text}</span>
              {(x.confidence || x.source) && <ConfChip confidence={x.confidence} label={x.source} />}
            </li>
          ))}
        </ul>
      )}
      <SlideBody section={s} firms={firms} entries={entries} save={save} />
      {s.id === "why_us" && <OfferPanel config={config} />}
    </>
  );
}

/** The offer card, its angles and its cold-test score (2.30, 2.31), under "Why they pick you" and on Settings › Your
 *  core Offer, where the workshop that changes it sits. */
export function OfferPanel({ config }: { config?: JsonObject }) {
  const { card, angles, score } = offerFromConfig(config);
  if (!card) {
    return (
      <p className="slide-hint">
        No offer card yet. <a href="/settings/business#offer-workshop">Run the offer workshop</a>: six questions, then a card tested on three busy owners.
      </p>
    );
  }
  return (
    <div className="pb-offer-card" aria-label="Offer card">
      <dl className="slide-facts">
        <div><dt>For</dt><dd>{card.for_whom}</dd></div>
        <div><dt>Their problem</dt><dd>“{card.problem_in_their_words}”</dd></div>
        <div><dt>What we offer</dt><dd>{card.what_we_offer}</dd></div>
        {card.proof.length > 0 && <div><dt>Proof</dt><dd>{card.proof.join(" · ")}</dd></div>}
        <div><dt>What you ask for first</dt><dd>{card.smallest_yes}</dd></div>
        {angles.length > 0 && (
          <div>
            <dt>Opening angles</dt>
            <dd>
              <ul className="plain-list">
                {angles.map((a) => <li key={a.key}><strong>{a.label}</strong> · {a.line}</li>)}
              </ul>
            </dd>
          </div>
        )}
        {score !== null && <div><dt>Cold test</dt><dd>{score}/10</dd></div>}
      </dl>
      <p className="slide-hint"><a href="/settings/business#offer-workshop">Open the offer workshop</a> to change it.</p>
    </div>
  );
}

function ConfChip({ confidence, label }: { confidence?: Confidence; label?: string }) {
  const guess = confidence === "guess";
  return <span className={`conf-chip${guess ? " is-guess" : ""}`}>{guess ? "Our guess" : (label ?? CONF_LABEL[confidence ?? "likely"])}</span>;
}

function SlideBody({ section: s, firms, entries, save }: { section: PlaybookSection; firms: Firm[]; entries: Map<string, Entry>; save: (f: Omit<PlaybookFeedbackInput, "version">) => Promise<boolean> }) {
  switch (s.kind) {
    case "segments":
      return (
        <>
          {s.segments.length > 0 ? (
            <ol className="slide-segments">
              {s.segments.map((g, i) => (
                <li key={`${g.name}-${i}`}>
                  <span className="seg-name">{g.name}</span>
                  {g.priority && <span className="seg-priority">{g.priority}</span>}
                  {g.detail && <span className="seg-detail">{g.detail}</span>}
                </li>
              ))}
            </ol>
          ) : (
            <p className="slide-hint">No customer groups yet. Who are your best customers?</p>
          )}
          {s.facts.length > 0 && (
            <dl className="slide-facts">
              {s.facts.map((f) => (
                <div key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </>
      );
    case "pools":
      return (
        <ul className="slide-pools">
          {s.pools.map((p) => (
            <li key={p.id} className={p.on ? "is-on" : "is-off"}>
              <span className="pool-state">{p.on ? "On" : "Off for now"}</span>
              <span className="pool-name">{p.name}</span>
              <span className="pool-feeds">{p.on && p.feeds.length ? p.feeds.join(" · ") : p.blurb}</span>
            </li>
          ))}
        </ul>
      );
    case "firms":
      return <FirmList firms={firms} entries={entries} save={save} />;
    case "voice":
      return (
        <div className="slide-voice">
          {s.email && (
            <figure className="sample-email">
              <figcaption className="sr-only">A sample first email</figcaption>
              {s.email.to && (
                <p className="email-meta">
                  <span>To</span> {s.email.to}
                </p>
              )}
              {s.email.subject && (
                <p className="email-meta">
                  <span>Subject</span> {s.email.subject}
                </p>
              )}
              <div className="email-body">{s.email.body}</div>
            </figure>
          )}
          {s.rules.length > 0 && (
            <ul className="slide-rules">
              {s.rules.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
          {(s.use.length > 0 || s.avoid.length > 0) && (
            <dl className="slide-facts">
              {s.use.length > 0 && (
                <div>
                  <dt>Words we use</dt>
                  <dd>{s.use.join(", ")}</dd>
                </div>
              )}
              {s.avoid.length > 0 && (
                <div>
                  <dt>Words we never use</dt>
                  <dd>{s.avoid.join(", ")}</dd>
                </div>
              )}
            </dl>
          )}
          {!s.email && <p className="slide-hint">A sample email appears here once the strategy agent has drafted one in your voice.</p>}
        </div>
      );
    case "journey":
      return (
        <>
          {s.stages.length > 0 && (
            <ol className="slide-journey">
              {s.stages.map((st) => (
                <li key={st.key} className={st.goal ? "is-goal" : st.after ? "is-after" : ""}>
                  <span className="journey-notch" aria-hidden="true" />
                  <span className="journey-label">{st.label}</span>
                  {st.goal && <span className="journey-note">Ogma's goal</span>}
                </li>
              ))}
            </ol>
          )}
          {s.deals.length > 0 && <p className="slide-hint">After that, your deals go through: {s.deals.join(" → ")}.</p>}
        </>
      );
    case "automation":
      return <GateList section={s} entries={entries} save={save} />;
    case "measures":
      return (
        <dl className="slide-measures">
          {s.measures.map((m) => (
            <div key={m.label}>
              <dt>{m.label}</dt>
              <dd className="measure-target">{m.target ?? "To agree"}</dd>
              {m.note && <dd className="measure-note">{m.note}</dd>}
            </div>
          ))}
        </dl>
      );
    default:
      return null;
  }
}

function FirmList({ firms, entries, save }: { firms: Firm[]; entries: Map<string, Entry>; save: (f: Omit<PlaybookFeedbackInput, "version">) => Promise<boolean> }) {
  if (!firms.length) return <p className="slide-hint">No firms found yet. Once Ogma starts looking, examples will appear here for you to say yes or no to.</p>;
  return (
    <ul className="slide-firms">
      {firms.map((f) => {
        const v = entries.get(keyOf("firm", "firms", f.id))?.verdict;
        const detail = [f.segment ? f.segment.replace(/_/g, " ") : null, f.city, f.tier ? `tier ${f.tier}` : null].filter(Boolean).join(" · ");
        return (
          <li key={f.id}>
            <div className="firm-text">
              <span className="firm-name">{f.name}</span>
              {detail && <span className="firm-detail">{detail}</span>}
            </div>
            <div className="seg-control" role="group" aria-label={`Would you want work from ${f.name}?`}>
              <button type="button" aria-pressed={v === "yes"} onClick={() => save({ kind: "firm", section: "firms", item: f.id, verdict: v === "yes" ? "clear" : "yes" })}>
                Yes
              </button>
              <button type="button" aria-pressed={v === "no"} onClick={() => save({ kind: "firm", section: "firms", item: f.id, verdict: v === "no" ? "clear" : "no" })}>
                No
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function GateList({ section, entries, save }: { section: Extract<PlaybookSection, { kind: "automation" }>; entries: Map<string, Entry>; save: (f: Omit<PlaybookFeedbackInput, "version">) => Promise<boolean> }) {
  return (
    <ul className="slide-gates">
      {section.gates.map((g) => {
        const wanted = entries.get(keyOf("gate", "gates", g.step))?.verdict as "approve" | "auto" | undefined;
        const mode = wanted ?? g.mode;
        return (
          <li key={g.step}>
            <span className="gate-label">
              {g.label}
              {wanted && wanted !== g.mode && <span className="gate-asked">You asked to change this</span>}
              {g.locked && g.step !== "0.5" && <span className="gate-fixed">Always comes to you</span>}
            </span>
            <div className="seg-control" role="group" aria-label={g.label}>
              <button type="button" aria-pressed={mode === "auto"} disabled={g.locked} onClick={() => save({ kind: "gate", section: "gates", item: g.step, verdict: g.mode === "auto" ? "clear" : "auto" })}>
                On its own
              </button>
              <button type="button" aria-pressed={mode === "approve"} disabled={g.locked} onClick={() => save({ kind: "gate", section: "gates", item: g.step, verdict: g.mode === "approve" ? "clear" : "approve" })}>
                I approve
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ------------------------------------------------------------------------------------------------ feedback
function VerdictBar({ section, verdict, save }: { section: PlaybookSection; verdict: Verdict | null; save: (f: Omit<PlaybookFeedbackInput, "version">) => Promise<boolean> }) {
  const [note, setNote] = useState("");
  const [spoken, setSpoken] = useState(false);
  const [saving, setSaving] = useState(false);
  const dictation = useDictation((t) => {
    setSpoken(true);
    setNote((n) => (n ? `${n.replace(/\s+$/, "")} ${t}` : t));
  });
  const noteId = `note-${section.id}`;

  const addNote = async () => {
    if (!note.trim()) return;
    if (dictation.listening) dictation.stop();
    setSaving(true);
    // A note on its own counts as "nearly" when the section has no verdict yet: something needs changing.
    const ok = await save({ kind: "section", section: section.id, note, via: spoken ? "voice" : "typed", ...(verdict ? {} : { verdict: "nearly" }) });
    setSaving(false);
    if (ok) {
      setNote("");
      setSpoken(false);
    }
  };

  return (
    <div className="verdict-bar">
      <div className="verdict-row">
        <span className="verdict-q" id={`q-${section.id}`}>
          Is this a fair description?
        </span>
        <div className="seg-control seg-control-lg" role="group" aria-labelledby={`q-${section.id}`}>
          {VERDICTS.map((v) => (
            <button key={v.key} type="button" aria-pressed={verdict === v.key} onClick={() => save({ kind: "section", section: section.id, verdict: verdict === v.key ? "clear" : v.key })}>
              {v.label}
            </button>
          ))}
        </div>
      </div>
      <div className="note-row">
        <label htmlFor={noteId} className="note-label">
          What would you change? Say it however you like.
        </label>
        <textarea
          id={noteId}
          rows={2}
          value={dictation.listening && dictation.interim ? `${note}${note ? " " : ""}${dictation.interim}` : note}
          onChange={(e) => setNote(e.target.value)}
          readOnly={dictation.listening}
          placeholder="For example: we stopped doing schools last year"
        />
        <div className="note-actions">
          {dictation.supported && (
            <button type="button" className={`btn btn-talk${dictation.listening ? " is-listening" : ""}`} aria-pressed={dictation.listening} onClick={dictation.listening ? dictation.stop : dictation.start}>
              <MicIcon />
              {dictation.listening ? "Stop" : "Talk"}
            </button>
          )}
          <button type="button" className="btn" onClick={addNote} disabled={!note.trim() || saving}>
            {saving ? "Saving…" : "Add note"}
          </button>
        </div>
        {dictation.error && <p className="note-error">{dictation.error}</p>}
      </div>
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="6.5" y="2" width="5" height="9" rx="2.5" />
      <path d="M3.5 8.5a5.5 5.5 0 0 0 11 0M9 14v2.5" />
    </svg>
  );
}

function SidePanel({ section, entry, prev, changed, onRemove }: { section: PlaybookSection; entry: Entry | undefined; prev: ReturnType<typeof previousNotes>; changed: boolean; onRemove: (index: number) => void }) {
  const last = prev?.bySection.get(section.id);
  return (
    <>
      {changed && <p className="side-changed">Changed in this draft. The detailed settings show exactly what.</p>}
      {last && (
        <SideBlock title={`Last time you said (version ${prev!.version})`}>
          {last.map((t, i) => (
            <p key={i} className="side-quote">
              {t}
            </p>
          ))}
        </SideBlock>
      )}
      <SideBlock title="Where this came from">
        <ul className="side-sources">
          {section.sources.map((s, i) => (
            <li key={i}>
              <span className="side-source-label">{s.label}</span>
              <span className={`conf-chip${s.confidence === "guess" ? " is-guess" : ""}`}>{CONF_LABEL[s.confidence]}</span>
              {s.note && <span className="side-source-note">{s.note}</span>}
            </li>
          ))}
        </ul>
      </SideBlock>
      <SideBlock title="Your notes">
        {entry?.notes.length ? (
          <ul className="side-notes">
            {entry.notes.map((n, i) => (
              <li key={i}>
                <span className="side-note-meta">
                  {n.via === "voice" ? "Spoken" : "Typed"}
                  {n.at ? ` · ${fmtDateTime(n.at)}` : ""}
                </span>
                {n.text}
                <button type="button" className="side-note-remove" onClick={() => onRemove(i)} aria-label={`Remove the note: ${n.text.slice(0, 40)}`}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">Nothing yet. Notes go to the strategy agent with your answers.</p>
        )}
      </SideBlock>
      {/* card-check: not a card (a short note at the foot of the slide's side panel) */}
      <details className="side-drives">
        <summary>What this sets up in Ogma</summary>
        <ul>
          {section.drives.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      </details>
    </>
  );
}

function SideBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="side-block">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

// ------------------------------------------------------------------------------------------------ sign off
function SignOff({
  pb,
  verdictOf,
  entries,
  crm,
  version,
  onDecided,
  setError,
  goTo,
  live,
}: {
  pb: Playbook;
  verdictOf: (id: string) => Verdict | null;
  entries: Map<string, Entry>;
  crm: Crm;
  version: number;
  onDecided: () => void;
  setError: (e: string | null) => void;
  goTo: (index: number) => void;
  live: boolean;
}) {
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState<"approve" | "send" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const unreviewed = pb.sections.map((s, i) => ({ s, i })).filter(({ s }) => !verdictOf(s.id));
  const counts = { spot_on: 0, nearly: 0, wrong: 0, open: 0 };
  for (const s of pb.sections) {
    const v = verdictOf(s.id);
    if (v) counts[v] += 1;
    else counts.open += 1;
  }
  const otherFeedback = [...entries.entries()].some(([k, e]) => (k.startsWith("firm:") || k.startsWith("gate:")) && e.verdict);
  const notes = [...entries.values()].some((e) => e.notes.length > 0);
  const toSend = counts.nearly + counts.wrong > 0 || notes || otherFeedback || comments.trim().length > 0;
  const allSpotOn = counts.spot_on === pb.sections.length && !notes;
  if (live) return <AskForChanges version={version} toSend={toSend} comments={comments} setComments={setComments} busy={busy === "send"} send={() => runSend()} />;
  async function runSend() {
    setBusy("send");
    setError(null);
    try {
      await crm.sendPlaybookFeedback(version, comments);
      onDecided();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  }

  const run = async (what: "approve" | "send") => {
    setBusy(what);
    setError(null);
    try {
      if (what === "approve") await crm.decideConfig(version, true);
      else await crm.sendPlaybookFeedback(version, comments);
      onDecided();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  };

  return (
    <div className="slide-signoff">
      <p className="slide-ref">Sign off</p>
      <h2 id="slide-title" className="slide-title">
        {allSpotOn ? "Everything is spot on" : "Where you've got to"}
      </h2>
      <dl className="signoff-counts">
        <div>
          <dt>Spot on</dt>
          <dd>{counts.spot_on}</dd>
        </div>
        <div>
          <dt>Nearly</dt>
          <dd>{counts.nearly}</dd>
        </div>
        <div>
          <dt>Wrong</dt>
          <dd>{counts.wrong}</dd>
        </div>
        <div>
          <dt>Not looked at</dt>
          <dd>{counts.open}</dd>
        </div>
      </dl>
      {allSpotOn ? (
        <p className="slide-lead">Approving switches this playbook on. You can change it any time; changes come back to you as a new draft.</p>
      ) : (
        <>
          <p className="slide-lead">
            Send your answers and notes and the strategy agent drafts version {version + 1}, changing only what you asked for and leaving the spot-on sections as they are. Or, if the differences don't matter, approve this version now.
          </p>
          <label className="note-label" htmlFor="signoff-comments">
            Anything else? (optional)
          </label>
          <textarea id="signoff-comments" rows={3} value={comments} onChange={(e) => setComments(e.target.value)} maxLength={5000} />
        </>
      )}
      {unreviewed.length > 0 && (
        <div className="signoff-open">
          <p>Not looked at yet:</p>
          <ul>
            {unreviewed.map(({ s, i }) => (
              <li key={s.id}>
                <button type="button" className="link-button" onClick={() => goTo(i)}>
                  {s.number} {s.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="action-bar">
        {allSpotOn ? (
          <button type="button" className="btn btn-primary" onClick={() => run("approve")} disabled={busy !== null}>
            {busy === "approve" ? "Approving…" : "Approve playbook"}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn"
              onClick={() => (unreviewed.length && !confirming ? setConfirming(true) : run("approve"))}
              disabled={busy !== null}
            >
              {busy === "approve" ? "Approving…" : confirming ? `Yes, approve without ${unreviewed.length === 1 ? "that section" : `those ${unreviewed.length} sections`}` : "Approve this version anyway"}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => run("send")} disabled={busy !== null || !toSend}>
              {busy === "send" ? "Sending…" : `Send feedback for version ${version + 1}`}
            </button>
          </>
        )}
      </div>
      {!allSpotOn && !toSend && <p className="muted small">Mark sections or add notes first, so the next draft knows what to change.</p>}
    </div>
  );
}

function AskForChanges({ version, toSend, comments, setComments, busy, send }: { version: number; toSend: boolean; comments: string; setComments: (v: string) => void; busy: boolean; send: () => void }) {
  return (
    <div className="slide-signoff">
      <p className="slide-ref">Ask for changes</p>
      <h2 id="slide-title" className="slide-title">
        What should change?
      </h2>
      <p className="slide-lead">
        Your marks and notes go to the strategy agent, which drafts version {version + 1}. It changes only what you asked for, and it comes back to you to approve. Until then Ogma keeps working from this version.
      </p>
      <label className="note-label" htmlFor="signoff-comments">
        Anything else? (optional)
      </label>
      <textarea id="signoff-comments" rows={3} value={comments} onChange={(e) => setComments(e.target.value)} maxLength={5000} placeholder="For example: we've taken on two estimators, so we can handle bigger jobs now" />
      <div className="action-bar">
        <button type="button" className="btn btn-primary" onClick={send} disabled={busy || !toSend}>
          {busy ? "Sending…" : `Ask for version ${version + 1}`}
        </button>
      </div>
      {!toSend && <p className="muted small">Mark a section or add a note first, so the next draft knows what to change.</p>}
    </div>
  );
}

function SettingsView({ draft, active }: { draft: ConfigVersion; active: JsonObject | null }) {
  const [onlyChanges, setOnlyChanges] = useState(Boolean(active));
  return (
    <div className="deck-settings">
      <p className="muted">Every setting in version {draft.version}, as the engine reads it. You don't need these to review the playbook; they are here if you want the detail.</p>
      {active && (
        <label className="check">
          <input type="checkbox" checked={onlyChanges} onChange={(e) => setOnlyChanges(e.target.checked)} /> Show only what changed
        </label>
      )}
      <ConfigSections config={draft.config} before={active} onlyChanges={onlyChanges} />
    </div>
  );
}
